import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, FolderKanban, ShoppingCart, Package, Warehouse, TrendingUp, LogOut, Menu, X, ClipboardList, IndianRupee, Receipt, FileText, UserPlus, Settings, Undo2, Wallet, BookOpen, ShieldCheck, ListChecks, ListTodo } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardLayout = ({ user }) => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [badges, setBadges] = useState({});

  useEffect(() => {
    if (user?.role !== 'admin') return;
    const load = () => {
      axios.get(`${BACKEND_URL}/api/admin/store-orders/counts`, { withCredentials: true })
        .then(r => setBadges(b => ({ ...b, store_new: r.data?.new || 0 })))
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [user]);

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login');
    }
  };

  const menuItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', testId: 'nav-dashboard', roles: ['admin', 'staff', 'vendor', 'client'] },
  ];

  // Role-specific menu items
  if (user?.role === 'admin' || user?.role === 'staff') {
    menuItems.push(
      { path: '/upcoming-tasks', icon: ListTodo, label: 'Upcoming Tasks', testId: 'nav-upcoming-tasks', roles: ['admin', 'staff'] },
      { path: '/customers', icon: Users, label: 'Customers', testId: 'nav-customers', roles: ['admin', 'staff'] },
      { path: '/projects', icon: FolderKanban, label: 'Projects', testId: 'nav-projects', roles: ['admin', 'staff'] },
      { path: '/orders', icon: ShoppingCart, label: 'Orders', testId: 'nav-orders', roles: ['admin', 'staff'] },
      { path: '/purchase-orders', icon: ClipboardList, label: 'Purchase Orders', testId: 'nav-purchase-orders', roles: ['admin', 'staff'] },
      { path: '/pending-deliveries', icon: Package, label: 'Pending Deliveries', testId: 'nav-pending-deliveries', roles: ['admin', 'staff'] },
      { path: '/vendors', icon: Package, label: 'Vendors', testId: 'nav-vendors', roles: ['admin'] },
      { path: '/products', icon: Warehouse, label: 'Products', testId: 'nav-products', roles: ['admin', 'staff'] },
      { path: '/inventory', icon: Warehouse, label: 'Inventory', testId: 'nav-inventory', roles: ['admin', 'staff'] }
    );
  }

  if (user?.role === 'client') {
    menuItems.push(
      { path: '/my-projects', icon: FolderKanban, label: 'My Projects', testId: 'nav-my-projects', roles: ['client'] },
      { path: '/my-orders', icon: ShoppingCart, label: 'My Orders', testId: 'nav-my-orders', roles: ['client'] }
    );
  }

  if (user?.role === 'vendor') {
    menuItems.push(
      { path: '/vendor-orders', icon: ShoppingCart, label: 'Orders', testId: 'nav-vendor-orders', roles: ['vendor'] },
      { path: '/vendor-products', icon: Warehouse, label: 'My Products', testId: 'nav-vendor-products', roles: ['vendor'] }
    );
  }

  if (user?.role === 'admin') {
    menuItems.push(
      { path: '/store-orders', icon: ShoppingCart, label: 'Online Orders', testId: 'nav-store-orders', roles: ['admin'], badgeKey: 'store_new' },
      { path: '/profit', icon: TrendingUp, label: 'Financials', testId: 'nav-profit', roles: ['admin'] },
      { path: '/payments', icon: IndianRupee, label: 'Payments', testId: 'nav-payments', roles: ['admin'] },
      { path: '/returns', icon: Undo2, label: 'Returns', testId: 'nav-returns', roles: ['admin'] },
      { path: '/expenses', icon: Receipt, label: 'Expenses', testId: 'nav-expenses', roles: ['admin'] },
      { path: '/partners', icon: Wallet, label: 'Partners', testId: 'nav-partners', roles: ['admin'] },
      { path: '/ledger', icon: BookOpen, label: 'Ledger', testId: 'nav-ledger', roles: ['admin'] },
      { path: '/audit', icon: ShieldCheck, label: 'Audit', testId: 'nav-audit', roles: ['admin'] },
      { path: '/task-templates', icon: ListChecks, label: 'Task Templates', testId: 'nav-task-templates', roles: ['admin'] },
      { path: '/quotation-builder', icon: FileText, label: 'Quotation Builder', testId: 'nav-quotation-builder', roles: ['admin'] },
      { path: '/leads', icon: UserPlus, label: 'Leads', testId: 'nav-leads', roles: ['admin'] },
      { path: '/portfolio-admin', icon: FileText, label: 'Portfolio', testId: 'nav-portfolio-admin', roles: ['admin'] },
      { path: '/business-settings', icon: Settings, label: 'Business Settings', testId: 'nav-business-settings', roles: ['admin'] },
      { path: '/users', icon: Users, label: 'User Management', testId: 'nav-users', roles: ['admin'] }
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 flex">
      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 h-screen bg-white border-r border-stone-200 z-50 transition-transform duration-300 ${
        mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      } w-64`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-stone-200">
            <h2 className="text-xl font-bold text-stone-900" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Nirmaan CRM
            </h2>
          </div>

          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {menuItems.filter(item => !item.roles || item.roles.includes(user?.role)).map((item) => (
              <button
                key={item.path}
                data-testid={item.testId}
                onClick={() => {
                  navigate(`/admin${item.path}`);
                  setMobileMenuOpen(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm text-stone-700 hover:bg-stone-50 rounded-md transition-all duration-200 hover:-translate-y-0.5"
              >
                <item.icon className="w-5 h-5" strokeWidth={1.5} />
                <span className="flex-1 text-left">{item.label}</span>
                {item.badgeKey && badges[item.badgeKey] > 0 && (
                  <span data-testid={`badge-${item.badgeKey}`} className="ml-auto bg-amber-700 text-white text-[10px] font-semibold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center">
                    {badges[item.badgeKey]}
                  </span>
                )}
              </button>
            ))}
          </nav>

          <div className="p-4 border-t border-stone-200">
            <div className="flex items-center gap-3 mb-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback>{user?.name?.charAt(0) || 'U'}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-stone-900 truncate">{user?.name}</p>
                <p className="text-xs text-stone-500 capitalize">{user?.role}</p>
              </div>
            </div>
            <Button
              data-testid="logout-btn"
              onClick={handleLogout}
              variant="outline"
              className="w-full flex items-center gap-2 text-stone-700 border-stone-200 hover:bg-stone-50"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile menu button */}
      <button
        data-testid="mobile-menu-toggle"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-md shadow-md border border-stone-200"
      >
        {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-stone-900/60 z-40"
          onClick={() => setMobileMenuOpen(false)}
        ></div>
      )}

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto p-6 lg:p-8">
          <Outlet context={{ user }} />
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;